import { useCallback } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { EngagementType } from '../../graphqlEnums'

export const DismissAllEngagementsByChannelIdsMutationGQL =
  graphql(/* GraphQL */ `
    mutation DismissAllEngagementsByChannelIds(
      $input: DismissAllEngagementsByChannelIdsInput!
    ) {
      dismissAllEngagementsByChannelIds(input: $input) {
        __typename
        ... on DismissAllEngagementsByChannelIdsSuccess {
          channels {
            id
            status
            dismissed
          }
        }
        ... on VoidMutationError {
          message
        }
      }
    }
  `)

export type DismissAllEngagementsByChannelIdsArgs = {
  channelIds: string[]
  engagementType: EngagementType
  toDate?: string
}

export type UseDismissAllEngagementsByChannelIdsResult = {
  dismissAllEngagementsByChannelIds: (
    args: DismissAllEngagementsByChannelIdsArgs,
  ) => Promise<void>
  loading: boolean
}

/**
 * Wraps the unified `dismissAllEngagementsByChannelIds` mutation (bulk dismiss).
 * Kind-agnostic — accepts `mention` or `comment` via `engagementType` — so the
 * eventual comments cutover slots in cleanly. Only `mention` is supported by the
 * backend at M1; comments still go through the legacy `dismissAllComments…`
 * mutations (see `hooks/comments/useDismissAll`).
 *
 * Mirrors `useDismissAllCommentsForChannels` in shape and toast copy. The toast
 * reports the dismissed totals the mutation returns, which can be lower than
 * the count the confirmation dialog showed: that count is capped per channel.
 */
export const useDismissAllEngagementsByChannelIds =
  (): UseDismissAllEngagementsByChannelIdsResult => {
    const organizationId = useOrganizationId()

    const [dismissAllMutation, { loading }] = useTypedMutation(
      DismissAllEngagementsByChannelIdsMutationGQL,
      (data) => data.dismissAllEngagementsByChannelIds,
      {
        successTypename: 'DismissAllEngagementsByChannelIdsSuccess',
        refetchQueries: ['MentionInbox', 'MentionCounts'],
      },
    )

    const dismissAllEngagementsByChannelIds = useCallback(
      async ({
        channelIds,
        engagementType,
        toDate,
      }: DismissAllEngagementsByChannelIdsArgs): Promise<void> => {
        if (!organizationId) {
          throw new Error('An organization id is required')
        }
        if (channelIds.length === 0) {
          throw new Error('At least one channel id is required')
        }

        try {
          const result = await dismissAllMutation({
            variables: {
              input: {
                organizationId,
                channelIds,
                engagementType,
                ...(toDate ? { toDate } : {}),
              },
            },
          })

          if (!result.success || !result.data) {
            toast.error('An error occurred while resolving the mentions')
            return
          }

          const totalDismissed = result.data.channels.reduce(
            (sum, channel) => sum + channel.dismissed,
            0,
          )

          if (totalDismissed === 0) {
            toast.success("We couldn't find any mention to resolve")
            return
          }

          const noun = totalDismissed === 1 ? 'mention' : 'mentions'
          const verb = totalDismissed === 1 ? 'was' : 'were'
          const dateClause = toDate ? ' older than 1 month' : ''

          toast.success(
            `${totalDismissed} ${noun}${dateClause} ${verb} resolved.`,
          )
        } catch {
          toast.error('An error occurred while resolving the mentions')
        }
      },
      [dismissAllMutation, organizationId],
    )

    return { dismissAllEngagementsByChannelIds, loading }
  }
